import { z } from "zod";

/**
 * A QR label's code (ADR 0018 › Codes): random characters from Crockford Base32,
 * stored uppercase. It says nothing about the garage or the part.
 */

/** Crockford Base32: no I, L, O or U, so a code read aloud or typed can't be misread. */
export const LABEL_CODE_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/**
 * How long a new code is. The length is the format version: raising it to 8 gives 32 times as many
 * codes, and the labels already printed keep working (ADR 0018 › Code range).
 */
export const LABEL_CODE_LENGTH = 7;

/** The lengths a code may have; the parser accepts 8 from day one. */
export const LABEL_CODE_LENGTHS: readonly number[] = [7, 8];

const CODE = new RegExp(`^[${LABEL_CODE_ALPHABET}]{7,8}$`);

/** Whether `code` is a code in its canonical form: uppercase, no separators. */
export function isLabelCode(code: string): boolean {
  return CODE.test(code);
}

/**
 * The canonical form of a code as typed, pasted or scanned: trimmed, uppercase, without `-` and
 * spaces, with O read as 0 and I and L as 1. Null when it still isn't a code.
 */
export function normalizeLabelCode(raw: string): string | null {
  const code = raw
    .trim()
    .toUpperCase()
    .replace(/[-\s]/g, "")
    .replaceAll("O", "0")
    .replace(/[IL]/g, "1");
  return isLabelCode(code) ? code : null;
}

/** A code as people read it, in two groups: `K2W-2T8M` (8 characters: `K2W2-T8MA`). */
export function groupLabelCode(code: string): string {
  const split = code.length - 4;
  return `${code.slice(0, split)}-${code.slice(split)}`;
}

/**
 * A new code from random bytes (the service takes them from a secure RNG). 32 divides 256, so
 * each byte's low five bits pick a character without bias.
 */
export function labelCodeFromBytes(bytes: Uint8Array, length = LABEL_CODE_LENGTH): string {
  if (bytes.length < length) throw new Error(`A code needs ${length} random bytes`);
  let code = "";
  for (let i = 0; i < length; i++) code += LABEL_CODE_ALPHABET[(bytes[i] ?? 0) & 31];
  return code;
}

/** A code as an API input: any spelling `normalizeLabelCode` reads, as its canonical form. */
export const labelCodeInput = z
  .string()
  .max(32)
  .transform((raw, ctx) => {
    const code = normalizeLabelCode(raw);
    if (code === null) {
      ctx.addIssue({ code: "custom", message: "validation.labelCode" });
      return z.NEVER;
    }
    return code;
  });
